import React, { useContext, useEffect, useState } from 'react';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import { HeaderType } from '../../components/Header/types/Header';
import { FooterType } from '../../components/Footer/types/Footer';
import BusinessProjectCard from '../../components/BusinessProjectCard/BusinessProjectCard';
import { Helmet } from 'react-helmet';
import styles from './BusinessPage.module.css'
import Loader from '../../components/Loader/Loader';
import { LoaderContext } from '../../components/Loader/LoaderContext';
import api from '../../utils/api';
import { transformBusinessProjectFeatures, transformBusinesses } from '../../utils/transformers';

interface Project {
  projectId: string;
  title?: string;
  description: string;
  preview: string;
  features: {
    id: number;
    text: string;
    image?: string;
  }[];
  image?: string;
  businessCount: number;
  minPrice?: number;
}

export default function BusinessPage() {
  const [projectsData, setProjectsData] = useState<Project[]>([]);
  const { isInitialLoad, setIsInitialLoad } = useContext(LoaderContext);

  useEffect(() => {
    document.documentElement.classList.add('black');
    document.body.classList.add('black');

    return () => {
      document.documentElement.classList.remove('black');
      document.body.classList.remove('black');
    };
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setIsInitialLoad(true);
      try {
        const businessProjectFeaturesResponse = await api.business.getProjectFeatures();
        const businessResponse = await api.business.getAll();
        
        const businessProjectFeatures = transformBusinessProjectFeatures(businessProjectFeaturesResponse);
        const businesses = transformBusinesses(businessResponse);

        const projects: Project[] = businessProjectFeatures.map((project) => {
          const availableBusinesses = businesses.filter((business) => 
            business.available === true && business.type === 'business'
          );
          const businessCount = availableBusinesses.length;
          let minPrice: number | undefined = undefined;
          
          if (availableBusinesses.length > 0) {
            const prices = availableBusinesses.map((business) => {
              const price = typeof business.price === 'string' ? parseFloat(business.price.replace(/ /g, '')) : parseFloat(business.price);
              const area = typeof business.area === 'string' ? parseFloat(business.area.replace(/ /g, '')) : parseFloat(business.area);
              const discountPerSqm = business.discountProperty ?? 0;
              const totalDiscount = discountPerSqm * area;
              let finalPrice = price - totalDiscount;
              if (finalPrice < 0) finalPrice = 0;
              return finalPrice;
            });
            const filteredPrices = prices.filter((price: number) => price > 0 && !isNaN(price));
            minPrice = filteredPrices.length > 0 ? Math.min(...filteredPrices) : undefined;
          }
          
          let features = Array.isArray(project.features) ? [...project.features] : [];
          if (minPrice !== undefined) {
            const formattedPrice = minPrice.toLocaleString('ru-RU');
            features.push({
              id: features.length + 1,
              text: `От ${formattedPrice} ₽`,
              image: undefined,
            });
          }
          
          return {
            projectId: project.projectId,
            title: project.title,
            description: project.description,
            preview: project.preview,
            features,
            image: project.image,
            businessCount,
            minPrice,
          };
        });

        setProjectsData(projects);
      } catch (error) {
        console.error('Ошибка при загрузке данных:', error);
        setProjectsData([]);
      } finally {
        setIsInitialLoad(false);
      }
    };

    fetchData();
  }, [setIsInitialLoad]);

  return (
    <div className={styles.page}>
      <Helmet>
        <title>Коммерция | Восток Строй</title>
        <meta name="author" content="Восток Строй" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://vostokstroy.com/projects" />
        <meta property="og:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <meta property="og:url" content="https://vostokstroy.com/projects" />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Восток Строй" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content="https://vostokstroy.com/open-graphs/main-opengraph.jpg" />
        <link rel="icon" type="image/svg+xml" href="/favicons/main-fav-32x32.svg" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicons/main-fav-16x16.png" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicons/main-fav-32x32.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/favicons/main-apple-touch-icon-180x180.png" />
        <link rel="icon" type="image/png" sizes="180x180" href="/favicons/main-fav-180x180.png" />
      </Helmet>
      {isInitialLoad ? (
        <Loader />
      ) : (
        <>
          <Header theme={HeaderType.Dark} />
          <div>
            {projectsData.map((project, index) => (
              <BusinessProjectCard
                key={project.projectId ?? index}
                project={project.projectId}
                title={project.title ?? ''}
                description={project.description}
                features={project.features}
                image={project.image}
                preview={project.preview}
                paddingVariant={index === 0 ? 'large' : 'default'}
                businessCount={project.businessCount}
              />
            ))}
          </div>
          <Footer theme={FooterType.Dark} />
        </>
      )}
    </div>
  );
}